import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  CloudUpload as BuildIcon,
  People as PeopleIcon,
  Assessment as StatsIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Analytics as AnalyticsIcon,
  BugReport as BugIcon,
  Error as ErrorIcon,
  Backup as BackupIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCurrentUser } from '../services/auth';
import { isGlobalAdmin } from '../utils/role';
import UserManagement from '../components/Admin/UserManagement';
import PlatformStatistics from '../components/Admin/PlatformStatistics';
import BackupManager from '../components/Admin/BackupManager';
import FeedbackManager from '../components/Admin/FeedbackManager';
import ErrorMonitoring from '../components/Admin/ErrorMonitoring';
import FeatureFlagManager from '../components/Admin/FeatureFlagManager';
import GlobalAdminConsole from '../components/Admin/GlobalAdminConsole';
import CaseAnalytics from '../components/Admin/CaseAnalytics';
import BuildManagement from '../components/Admin/BuildManagement';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Stable keys let other pages deep-link back into a tab, e.g. `/admin?tab=users`.
const tabs = [
  { key: 'console', icon: <SecurityIcon />, label: 'Global Console', component: GlobalAdminConsole },
  { key: 'users', icon: <PeopleIcon />, label: 'User Management', component: UserManagement },
  { key: 'analytics', icon: <StatsIcon />, label: 'Platform Analytics', component: PlatformStatistics },
  { key: 'builds', icon: <BuildIcon />, label: 'Build Management', component: BuildManagement },
  { key: 'backups', icon: <BackupIcon />, label: 'Backups', component: BackupManager },
  { key: 'case-analytics', icon: <AnalyticsIcon />, label: 'Case Analytics', component: CaseAnalytics },
  { key: 'feedback', icon: <BugIcon />, label: 'Feedback Management', component: FeedbackManager },
  { key: 'errors', icon: <ErrorIcon />, label: 'Error Monitoring', component: ErrorMonitoring },
  { key: 'feature-flags', icon: <ToggleOnIcon />, label: 'Feature Flags', component: FeatureFlagManager }
];

function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = tabs.findIndex((tab) => tab.key === searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState(requestedTab >= 0 ? requestedTab : 0);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    if (!isGlobalAdmin(user)) {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const key = tabs[newValue]?.key;
    setSearchParams(!key || key === 'console' ? {} : { tab: key }, { replace: true });
  };

  if (!isGlobalAdmin(user)) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <AdminIcon sx={{ fontSize: 40, mr: 2, color: '#16a085' }} />
        <Typography variant="h3" component="h1">
          Admin Panel
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 4 }}>
        <AlertTitle>Global administration</AlertTitle>
        Manage universities, billing, platform analytics, and operational tooling from a single hub.
      </Alert>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {tabs.map((tab, index) => (
              <Tab
                key={tab.key}
                icon={tab.icon}
                label={tab.label}
                id={`admin-tab-${index}`}
                aria-controls={`admin-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {tabs.map((tab, index) => (
          <TabPanel key={tab.key} value={activeTab} index={index}>
            <tab.component />
          </TabPanel>
        ))}
      </Paper>
    </Container>
  );
}

export default AdminPage;
